import React, { forwardRef, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { useOutsideClick } from 'rooks';

import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowUp from 'icons/icon-arrow-up';

import './OverlayElement.styles.css';

const popperModifiers = [
    {
        name: 'offset',
        enabled: true,
        options: {
            offset: [0, 6],
        }
    }
];

export interface OverlayElementProps {
    className?: string,
    inheritWidth?: boolean,
    overflowPadding?: number,
    placement: 'auto-end' | 'auto-start' | 'auto' | 'bottom-end' | 'bottom-start' | 'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right' | 'top-end' | 'top-start' | 'top',
    refElement: HTMLElement | null,
    onOutsideClick?: (event: MouseEvent) => void,
    children: React.ReactNode
}

const OverlayElement = ({ className = '', inheritWidth, overflowPadding = 20, placement, refElement, onOutsideClick, children }: OverlayElementProps) => {
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
    const popperElementRef = useRef(popperElement);
    const { styles, attributes, update } = usePopper(refElement, popperElement, {
        modifiers: [
            ...popperModifiers,
            {
                name: 'preventOverflow',
                enabled: true,
                options: {
                    rootBoundary: 'window',
                    padding: overflowPadding,
                }
            },
            {
                name: 'arrow',
                options: { element: arrowElement }
            }
        ],
        placement
    });

    popperElementRef.current = popperElement;

    const handleOutsideClick = (event: MouseEvent) => {
        onOutsideClick?.(event);
    }

    useOutsideClick(popperElementRef, handleOutsideClick);

    useEffect(() => {
        update?.();
    }, [children]);

    return ReactDOM.createPortal(
        <div
            className={'overlay-element ' + className}
            ref={setPopperElement}
            style={{
                ...styles.popper,
                width: inheritWidth ? refElement?.clientWidth : undefined
            }}
            {...attributes.popper}
        >
            { children }

            <div className="overlay-element-arrow" ref={setArrowElement} style={styles.arrow} />
        </div>,
        document.body
    );
}

export default OverlayElement;

interface OverlayPaneProps {
    id?: string,
    className?: string,
    onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void,
    onMouseMove?: (event: React.MouseEvent<HTMLDivElement>) => void,
    children: React.ReactNode
}

export const OverlayPane = forwardRef<HTMLDivElement, OverlayToggleProps>(({ id, className = '', onMouseDown, onMouseMove, children }: OverlayPaneProps, ref) => (
    <div ref={ref} className={'overlay-pane ' + className} onMouseDown={onMouseDown} onMouseMove={onMouseMove}>
        { children }
    </div>
));


interface OverlayToggleProps {
    className?: string,
    styled?: boolean,
    visible?: boolean,
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
    children: React.ReactNode,
}

export const OverlayToggle = forwardRef<HTMLDivElement, OverlayToggleProps>(({ className = '', styled, visible, onClick, children }, ref) => (
    <div className={(styled ? 'overlay-toggle ' : '' ) + className} ref={ref} onClick={onClick}>
        { children }

        { visible ? <IconArrowUp /> : <IconArrowDown /> }
    </div>
));
